<template>
  <div
    class="annotation-toolbar"
    :class="{ dragging: isDragging }"
    :style="{ transform: `translate(${position.x}px, ${position.y}px)` }"
  >
    <template v-if="annotationMode">
      <v-tooltip text="Pārvietot" location="top" :disabled="isDragging" :z-index="10000">
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            flat
            variant="text"
            size="small"
            class="tool-btn drag-btn"
            :class="{ active: isDragging }"
            @pointerdown.prevent="startDrag"
          >
            <v-icon icon="fa-regular fa-grip-dots-vertical" />
          </v-btn>
        </template>
      </v-tooltip>
      <v-tooltip text="Zīmēt" location="top" :disabled="isDragging" :z-index="10000">
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            flat
            variant="text"
            size="small"
            class="tool-btn"
            :class="{ active: !isErasing && !isDragging && !isDrawingFilledRectangle && !isDrawingInverseFilledRectangle }"
            @click="enableDrawing"
          >
            <v-icon icon="fa-regular fa-paintbrush-pencil" />
          </v-btn>
        </template>
      </v-tooltip>
      <v-tooltip text="Mainīt krāsu" location="top" :disabled="isDragging" :z-index="10000">
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            flat
            variant="text"
            size="small"
            class="tool-btn"
            @click="toggleColorPicker"
          >
            <v-icon icon="fa-solid fa-palette" :style="{ color: drawColor }" />
          </v-btn>
        </template>
      </v-tooltip>
      <v-menu v-model="colorPickerOpen" location="top" :close-on-content-click="false" :offset="10" :z-index="10000">
        <template #activator="{ props }">
          <div v-bind="props"></div>
        </template>
        <v-card>
          <v-color-picker v-model="drawColor" mode="hex" hide-inputs></v-color-picker>
        </v-card>
      </v-menu>
      <v-tooltip text="Mainīt izmēru" location="top" :disabled="isDragging" :z-index="10000">
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            flat
            variant="text"
            size="small"
            class="tool-btn"
            @click="toggleSizeSlider"
          >
            <v-icon icon="fa-solid fa-arrow-up-big-small" />
          </v-btn>
        </template>
      </v-tooltip>
      <v-menu v-model="sizeSliderOpen" location="top" :close-on-content-click="false" content-class="slider-menu-content" :offset="10" :z-index="10000">
        <template #activator="{ props }">
          <div v-bind="props"></div>
        </template>
        <v-card class="px-4 py-1 slider-card">
          <v-slider
            v-model="lineWidth"
            :min="1"
            :max="20"
            step="1"
            thumb-label
            hide-details
            track-size="3"
            thumb-size="12"
          ></v-slider>
        </v-card>
      </v-menu>
      <v-tooltip text="Paslēpt" location="top" :disabled="isDragging" :z-index="10000">
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            flat
            variant="text"
            size="small"
            class="tool-btn"
            :class="{ active: isDrawingFilledRectangle && !isDragging }"
            @click="toggleRectangleTool"
          >
            <v-icon icon="fa-solid fa-square" />
          </v-btn>
        </template>
      </v-tooltip>
      <v-tooltip text="Izcelt" location="top" :disabled="isDragging" :z-index="10000">
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            flat
            variant="text"
            size="small"
            class="tool-btn"
            :class="{ active: isDrawingInverseFilledRectangle && !isDragging }"
            @click="toggleInverseRectangleTool"
          >
            <v-icon icon="fa-duotone fa-solid fa-draw-square" />
          </v-btn>
        </template>
      </v-tooltip>
      <v-tooltip text="Dzēst" location="top" :disabled="isDragging" :z-index="10000">
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            flat
            variant="text"
            size="small"
            class="tool-btn"
            :class="{ active: isErasing && !isDragging }"
            @click="toggleEraser"
          >
            <v-icon icon="fa-solid fa-eraser" />
          </v-btn>
        </template>
      </v-tooltip>
      <v-tooltip text="Atcelt visu" location="top" :disabled="isDragging" :z-index="10000">
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            flat
            variant="text"
            size="small"
            class="tool-btn"
            @click="clearCanvas"
          >
            <v-icon icon="fa-solid fa-trash" />
          </v-btn>
        </template>
      </v-tooltip>
      <v-tooltip text="Aizvērt" location="top" :disabled="isDragging" :z-index="10000">
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            flat
            variant="text"
            size="small"
            class="tool-btn close-btn"
            @click="closeAnnotationMode"
          >
            <v-icon icon="fa-solid fa-xmark" />
          </v-btn>
        </template>
      </v-tooltip>
    </template>
    <v-btn
      v-if="!annotationMode"
      flat
      variant="outlined"
      class="annotation-btn"
      @click="handleToggle"
    >
      <v-icon icon="fa-regular fa-paintbrush-pencil" class="mr-2" />
      Anotēt
    </v-btn>
  </div>
</template>

<script>
import { reactive, onUnmounted, computed } from "vue"

import { useImageAnnotation } from "~/composables/useImageAnnotation"
import { useAnnotationStore } from "~/stores/annotationStore"

export default {
  name: "AnnotationToolbar",
  emits: ["drag"],
  setup(_props, { emit }) {
    const annotation = useImageAnnotation()
    const annotationStore = useAnnotationStore()

    const isDragging = computed(() => annotationStore.isDragging)
    const position = reactive({ x: 0, y: 0 })
    let dragStart = { x: 0, y: 0 }
    let initialPosition = { x: 0, y: 0 }

    const onDrag = (e) => {
      if (!isDragging.value) {
        return
      }
      const dx = e.clientX - dragStart.x
      const dy = e.clientY - dragStart.y
      position.x = initialPosition.x + dx
      position.y = initialPosition.y + dy
      emit("drag")
    }

    const stopDrag = (e) => {
      annotationStore.setIsDragging(false)
      if (e.target && e.target.releasePointerCapture) {
        try {
          e.target.releasePointerCapture(e.pointerId)
        } catch (error) {
          // Ignore capture errors
        }
      }
      window.removeEventListener("pointermove", onDrag)
      window.removeEventListener("pointerup", stopDrag)
    }

    const startDrag = (e) => {
      annotationStore.setIsDragging(true)
      dragStart = { x: e.clientX, y: e.clientY }
      initialPosition = { x: position.x, y: position.y }

      if (e.target && e.target.setPointerCapture) {
        try {
          e.target.setPointerCapture(e.pointerId)
        } catch (error) {
          // Ignore capture errors (often happens with re-dispatched events)
        }
      }

      window.addEventListener("pointermove", onDrag)
      window.addEventListener("pointerup", stopDrag)
    }

    onUnmounted(() => {
      window.removeEventListener("pointermove", onDrag)
      window.removeEventListener("pointerup", stopDrag)
    })

    return {
      ...annotation,
      isDragging,
      position,
      startDrag,
      handleToggle() {
        if (annotationStore.annotationMode) {
          annotation.toggleEraser()
        } else {
          annotationStore.toggleAnnotationMode()
        }
      },
      enableDrawing() {
        if (annotationStore.isErasing) {
          annotation.toggleEraser()
        }
        if (annotationStore.isDrawingFilledRectangle) {
          annotation.toggleRectangleTool()
        }
        if (annotationStore.isDrawingInverseFilledRectangle) {
          annotation.toggleInverseRectangleTool()
        }
      },
      closeAnnotationMode() {
        annotationStore.toggleAnnotationMode()
      },
    }
  },
}
</script>

<style lang="sass" scoped>
.annotation-toolbar
  display: flex
  justify-content: center
  align-items: center
  background-color: white
  margin-top: 1rem
  border-radius: 50px
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1)
  width: fit-content
  position: relative
  z-index: 20
  pointer-events: auto
  cursor: pointer
  transition: transform 0.1s ease-out, box-shadow 0.2s ease

  &.dragging
    cursor: grabbing
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15)
    transition: none !important

  .annotation-btn
    border: none
    text-transform: none
    letter-spacing: normal
    height: 45px
    font-size: 15px
    border-radius: 50px

  .tool-btn
    display: flex
    flex-direction: column
    align-items: center
    justify-content: center
    min-width: 14px
    width: 14px
    min-height: 14px
    height: 14px
    padding: 18px
    font-size: 15px
    text-transform: none
    letter-spacing: normal
    border-radius: 50%
    cursor: pointer

    &.drag-btn
      cursor: move

    &:first-of-type
      margin: 5px 0 5px 5px

    &:last-of-type
      margin: 5px 5px 5px 0

    &.active
      background-color: #FFF1AE

    &.toggle-btn.active
      background-color: #FFF1AE

    &:disabled
      opacity: 0.4

  .slider-card
    min-width: 200px
    width: 200px
    overflow: visible !important

    :deep(.v-slider)
      width: 100%

    :deep(.v-slider__container)
      width: 100%

:deep(.slider-menu-content)
  min-width: 250px !important
  overflow: visible !important

  .v-card,
  .v-sheet,
  .v-list
    overflow: visible !important
</style>
